import TimePicker, { CustomTimePickerProps } from '../../inputs/TimePicker';
import { isValid as isValidDate } from 'date-fns';
import { useRef } from 'react';

import { TID_ATTR } from '@/common/infrastructure/config/tid';
import { BaseFormikFieldProps } from './types/FormikFieldProps';
import { FormikFieldSkeleton } from './FormikFieldSkeleton';
import { useFormikField } from './hooks/useFormikField';

/**
 * FormikTimePicker component that integrates TimePicker with Formik.
 * Shorthand parsing is handled by the TimePicker component itself (on Tab key).
 */
export const FormikTimePicker = <T extends Record<string, unknown>>({
  id,
  label,
  testId: testIdProp,
  ...rest
}: BaseFormikFieldProps<T> & CustomTimePickerProps & { testId?: string }) => {
  const {
    [TID_ATTR]: restTestId,
    slotProps: restSlotProps,
    onChange: externalOnChange,
    ...restWithoutTestId
  } = rest as Record<string, unknown> & {
    [TID_ATTR]?: string;
    slotProps?: { textField?: Record<string, unknown> };
    onChange?: (date: Date | null) => void;
  };
  const baseTestId = testIdProp ?? restTestId;
  const { status, setFieldValue, handleBlur, errorText, hasError, fieldValue } = useFormikField<T>({ id });
  const inputRef = useRef<HTMLInputElement | null>(null);

  if (status?.loading) {
    return <FormikFieldSkeleton />;
  }

  // Ensure value is never undefined (fix controlled/uncontrolled warning)
  const controlledValue = fieldValue === undefined ? null : fieldValue;

  const existingTextField = (restSlotProps?.textField ?? {}) as Record<string, unknown>;
  const existingInputProps = (existingTextField.inputProps ?? {}) as Record<string, unknown>;
  const existingSlotProps = (existingTextField.slotProps ?? {}) as Record<string, Record<string, unknown>>;

  const textFieldSlotProps = baseTestId
    ? {
        slotProps: {
          ...existingSlotProps,
          inputLabel: { ...existingSlotProps.inputLabel, [TID_ATTR]: `${baseTestId}:label` },
          htmlInput: { ...existingSlotProps.htmlInput, [TID_ATTR]: `${baseTestId}:input` },
          formHelperText: { ...existingSlotProps.formHelperText, [TID_ATTR]: `${baseTestId}:helper-text` }
        },
        inputProps: {
          ...existingInputProps,
          [TID_ATTR]: `${baseTestId}:input`
        }
      }
    : {};

  const openPickerButtonSlotProps = baseTestId ? { [TID_ATTR]: `${baseTestId}:open-button` } : undefined;

  const pickerPopupSlotProps = baseTestId
    ? {
        popper: { [TID_ATTR]: `${baseTestId}:popper` },
        desktopPaper: { [TID_ATTR]: `${baseTestId}:desktop-paper` },
        layout: { [TID_ATTR]: `${baseTestId}:layout` }
      }
    : undefined;

  return (
    <TimePicker
      fullWidth
      id={id}
      name={id}
      label={label}
      value={controlledValue as Date | null}
      onChange={(date) => {
        const normalized = date && isValidDate(date) ? date : null;
        setFieldValue(id, normalized);
        externalOnChange?.(normalized);
      }}
      onBlur={handleBlur}
      error={hasError}
      helperText={errorText}
      {...restWithoutTestId}
      slotProps={
        {
          ...restSlotProps,
          ...(openPickerButtonSlotProps && { openPickerButton: openPickerButtonSlotProps }),
          ...pickerPopupSlotProps,
          textField: {
            ...existingTextField,
            inputRef: inputRef,
            ...textFieldSlotProps
          } as unknown as CustomTimePickerProps['slotProps']
        } as unknown as CustomTimePickerProps['slotProps']
      }
    />
  );
};
